import useEmblaCarousel from 'embla-carousel-react'
import {
  BookmarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon,
  MessageCircleIcon,
  SendIcon,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { cn } from '@/lib/cn'
import { contenidoNoVisible } from '@/lib/schemas/revision'
import { MediaSlot } from './MediaSlot'

interface Archivo {
  id: string
  rol: 'principal' | 'lamina' | 'portada'
  orden: number
  driveFileId: string
  blurhash: string | null
  miniatura: string | null
  mime: string
}

interface VistaPiezaProps {
  /** El @ del cliente: se carga en Administración. */
  usuarioInstagram: string
  tipo: 'imagen' | 'carrusel' | 'video'
  copy: string
  archivos: Archivo[]
  /** Se avisa qué lámina se está mirando: el comentario la precarga (§8.4). */
  alCambiarLamina?: (n: number) => void
  /**
   * Sólo RASTRO puede reemplazar un archivo que no se ve (§8.4). Que venga
   * también cambia el aviso: a RASTRO no se le pide que avise a RASTRO.
   */
  alReemplazar?: () => void
}

/**
 * Alto máximo de la pieza. Más alto en escritorio: la pieza tiene que verse
 * cerca de su tamaño real (revisión OO del 23/09/2026: en el portal se veía
 * bastante más chica que en Drive). Desde 1024 px, además, el posteo entero
 * —@, pieza, íconos y copy— entra en la pantalla sin desplazar: la pieza
 * usa lo que dejan el modal y lo demás (19rem), hasta el 70 % del alto.
 */
const ALTO = 'var(--alto-pieza)'

/**
 * C3 · columna izquierda: la pieza como un posteo de Instagram (§8.4).
 *
 * Revisión OO del 23/09/2026: la tarjeta con borde que envolvía el posteo
 * achicaba la pieza. Se quitó la tarjeta, no el aspecto de Instagram: la
 * cabecera con el @ del cliente, los íconos y el copy con el @ se alinean al
 * ancho de la pieza, sobre el fondo del panel, y la pieza llega hasta el 70 %
 * del alto de la pantalla en escritorio. El carrusel se desliza con el dedo y
 * precarga las láminas contiguas.
 */
export function VistaPieza({
  usuarioInstagram,
  tipo,
  copy,
  archivos,
  alCambiarLamina,
  alReemplazar,
}: VistaPiezaProps) {
  const laminas = archivos
    .filter((a) => a.rol !== 'portada')
    .sort((a, b) => a.orden - b.orden)
  const portada = archivos.find((a) => a.rol === 'portada') ?? null
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, align: 'start' })
  const [actual, setActual] = useState(0)
  const [reproduciendo, setReproduciendo] = useState(false)
  /** Láminas que no se pueden ver, contadas desde 1; 0 = la pieza entera. */
  const [noCargan, setNoCargan] = useState<number[]>([])
  const noCarga = (n: number) => {
    setNoCargan((xs) => (xs.includes(n) ? xs : [...xs, n].sort((a, b) => a - b)))
  }
  const [copyAbierto, setCopyAbierto] = useState(false)
  // La pieza se ve en la proporción de su archivo, sin recortar; un carrusel
  // entero toma la de la primera lámina, como Instagram (decisión 21/09/2026).
  const [proporcion, setProporcion] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (!embla) return
    const alSeleccionar = () => {
      const n = embla.selectedScrollSnap()
      setActual(n)
      alCambiarLamina?.(n + 1)
    }
    embla.on('select', alSeleccionar)
    return () => {
      embla.off('select', alSeleccionar)
    }
  }, [embla, alCambiarLamina])

  const anterior = useCallback(() => embla?.scrollPrev(), [embla])
  const siguiente = useCallback(() => embla?.scrollNext(), [embla])

  const sinArchivos = laminas.length === 0

  return (
    <div
      data-vista-pieza
      className="flex flex-col gap-3 [--alto-pieza:60vh] sm:[--alto-pieza:70vh] lg:[--alto-pieza:min(70vh,calc(100dvh_-_19rem))]"
    >
      {/* El posteo toma el ancho de la pieza: sin franjas a los costados. */}
      <article
        className="mx-auto flex w-full flex-col gap-2"
        style={
          proporcion
            ? { width: `min(100%, calc(${ALTO} * ${proporcion.toFixed(4)}))` }
            : undefined
        }
      >
        <header data-cabecera-ig className="flex items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-borde text-xs font-semibold">
            {usuarioInstagram.slice(0, 2).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">
            @{usuarioInstagram}
          </span>
        </header>

        <div className="relative w-full overflow-hidden rounded-lg bg-tinta/5">
          {sinArchivos ? (
            <MediaSlot aspect="4:5" />
          ) : tipo === 'carrusel' ? (
            <>
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                  {laminas.map((a, i) => (
                    <div key={a.id} className="min-w-0 shrink-0 grow-0 basis-full">
                      <MediaSlot
                        aspect="archivo"
                        altoMaximo={ALTO}
                        proporcion={i === 0 ? undefined : proporcion}
                        alMedir={
                          i === 0
                            ? (r) => {
                                setProporcion(r)
                              }
                            : undefined
                        }
                        driveFileId={a.driveFileId}
                        mime={a.mime}
                        blurhash={a.blurhash ?? undefined}
                        miniaturaGuardada={a.miniatura}
                        ancho={1200}
                        // Se precargan las contiguas para que el deslizamiento no espere.
                        mode={Math.abs(i - actual) <= 1 ? 'playing' : 'poster'}
                        titulo={`Lámina ${String(i + 1)}`}
                        alFallar={() => {
                          noCarga(i + 1)
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {laminas.length > 1 && (
                <>
                  <Boton
                    variante="secundario"
                    tamano="icono"
                    aria-label="Lámina anterior"
                    disabled={actual === 0}
                    onClick={anterior}
                    className="absolute top-1/2 left-2 size-9 -translate-y-1/2 bg-tarjeta/90"
                  >
                    <ChevronLeftIcon className="size-5" aria-hidden />
                  </Boton>
                  <Boton
                    variante="secundario"
                    tamano="icono"
                    aria-label="Lámina siguiente"
                    disabled={actual === laminas.length - 1}
                    onClick={siguiente}
                    className="absolute top-1/2 right-2 size-9 -translate-y-1/2 bg-tarjeta/90"
                  >
                    <ChevronRightIcon className="size-5" aria-hidden />
                  </Boton>
                  <span
                    data-contador-laminas
                    className="absolute top-3 right-3 rounded-full bg-tinta/70 px-2.5 py-1 text-xs font-medium text-white"
                  >
                    {actual + 1} / {laminas.length}
                  </span>
                </>
              )}
            </>
          ) : tipo === 'video' ? (
            <MediaSlot
              // Portada y video son dos marcos distintos (OBS-006): si el del
              // video heredaba la medida de la portada, el visor de Drive nacía
              // con esa proporción y el reproductor del iPhone no se reacomodaba.
              key={reproduciendo ? 'video' : 'portada'}
              aspect="archivo"
              altoMaximo={ALTO}
              driveFileId={(reproduciendo ? laminas[0] : portada)?.driveFileId}
              mime={(reproduciendo ? laminas[0] : portada)?.mime}
              blurhash={(reproduciendo ? laminas[0] : portada)?.blurhash ?? undefined}
              miniaturaGuardada={(reproduciendo ? laminas[0] : portada)?.miniatura}
              ancho={1200}
              mode="playing"
              titulo={reproduciendo ? 'Video' : 'Portada del video'}
              alMedir={(r) => {
                setProporcion(r)
              }}
              alFallar={() => {
                noCarga(0)
              }}
            />
          ) : (
            <MediaSlot
              aspect="archivo"
              altoMaximo={ALTO}
              driveFileId={laminas[0]?.driveFileId}
              mime={laminas[0]?.mime}
              blurhash={laminas[0]?.blurhash ?? undefined}
              miniaturaGuardada={laminas[0]?.miniatura}
              ancho={1200}
              mode="playing"
              titulo="Contenido de la publicación"
              alMedir={(r) => {
                setProporcion(r)
              }}
              alFallar={() => {
                noCarga(0)
              }}
            />
          )}

          {tipo === 'video' && !reproduciendo && laminas.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-tinta/20">
              <Boton
                onClick={() => {
                  setReproduciendo(true)
                }}
              >
                Reproducir
              </Boton>
            </div>
          )}
        </div>

        {/* Íconos de Instagram, con los puntos del carrusel en el medio. */}
        <div
          className="grid grid-cols-[1fr_auto_1fr] items-center text-tinta-suave"
          aria-hidden
        >
          <div className="flex items-center gap-3.5">
            <HeartIcon className="size-5" />
            <MessageCircleIcon className="size-5" />
            <SendIcon className="size-5" />
          </div>
          <div className="flex justify-center gap-1.5">
            {tipo === 'carrusel' &&
              laminas.length > 1 &&
              laminas.map((a, i) => (
                <span
                  key={a.id}
                  className={cn(
                    'size-1.5 rounded-full',
                    i === actual ? 'bg-tinta' : 'bg-borde',
                  )}
                />
              ))}
          </div>
          <BookmarkIcon className="size-5 justify-self-end" />
        </div>

        <div data-copy>
          <p
            className={cn(
              'text-sm whitespace-pre-wrap',
              !copyAbierto && 'line-clamp-3 lg:line-clamp-2',
            )}
          >
            <span className="font-semibold">@{usuarioInstagram}</span>{' '}
            {copy || <span className="text-tinta-suave">Sin copy.</span>}
          </p>
          {copy.length > 80 && (
            <button
              type="button"
              className="mt-1 text-sm text-tinta-suave hover:text-tinta"
              onClick={() => {
                setCopyAbierto((x) => !x)
              }}
            >
              {copyAbierto ? 'menos' : 'más'}
            </button>
          )}
        </div>
      </article>

      {/* §8.4 — si el contenido no carga, el resto del panel sigue igual. */}
      {noCargan.length > 0 && (
        <p
          role="status"
          className="rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta"
        >
          {contenidoNoVisible(noCargan, { paraRastro: Boolean(alReemplazar) })}
          {alReemplazar && (
            <Boton
              variante="sutil"
              tamano="chico"
              className="ml-2"
              onClick={alReemplazar}
            >
              Reemplazar archivo
            </Boton>
          )}
        </p>
      )}
    </div>
  )
}
